import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'src/lib/routing';

import type { ReactNode } from 'react';

const STORAGE_KEY = 'main_nav_sidebar_state';
const MOBILE_BREAKPOINT = 768;

type SidebarContextValue = readonly [boolean, () => void];

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

export const SidebarCollapsedProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    if (isMobile) {
      return true;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  const isCollapsedRef = useRef(isCollapsed);
  useEffect(() => {
    isCollapsedRef.current = isCollapsed;
  }, [isCollapsed]);

  const shouldAutoCollapse = useMemo(() => {
    // Only apply route-specific logic on desktop
    if (isMobile) {
      return false;
    }

    const path = location.pathname;
    const basePaths = ['/studies', '/bookings', '/booking-setup'];

    return basePaths.some(basePath => {
      if (path === basePath) {
        return false;
      }
      return path.startsWith(basePath + '/');
    });
  }, [location.pathname, isMobile]);

  // Auto-close sidebar on mobile when navigating to any route
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  }, [location.pathname, isMobile]);

  // Route-specific behavior for desktop only
  useEffect(() => {
    if (isMobile) {
      return;
    }

    if (shouldAutoCollapse) {
      setIsCollapsed(true);
    } else if (location.pathname === '/studies') {
      setIsCollapsed(false);
    }
  }, [shouldAutoCollapse, location.pathname, isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const checkScreenSize = () => {
      const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(newIsMobile);

      if (newIsMobile && !isCollapsedRef.current) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Only persist to localStorage on desktop
  useEffect(() => {
    if (typeof window === 'undefined' || isMobile) {
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed, isMobile]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const value = useMemo(() => [isCollapsed, toggleCollapsed] as const, [isCollapsed, toggleCollapsed]);

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebarCollapsed = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarCollapsed must be used within SidebarCollapsedProvider');
  }
  return context;
};
